import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Table, Badge, ProgressBar } from 'react-bootstrap';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import analyticsService from '../services/analyticsService';
import SEOHead from '../components/SEOHead';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await analyticsService.getDashboard();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getChartColors = () => ({
    primary: '#007bff',
    success: '#28a745',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#17a2b8',
    secondary: '#6c757d'
  });

  const renderOverviewCards = () => {
    if (!analytics) return null;

    const cards = [
      {
        title: 'Total Surveys',
        value: analytics.totalSurveys,
        icon: 'fas fa-poll',
        color: 'primary',
        subtitle: `${analytics.activeSurveys} active`
      },
      {
        title: 'Total Responses',
        value: analytics.totalResponses,
        icon: 'fas fa-chart-line',
        color: 'success',
        subtitle: 'Completed responses'
      },
      {
        title: 'Total Users',
        value: analytics.totalUsers,
        icon: 'fas fa-users',
        color: 'info',
        subtitle: `${analytics.userAnalytics?.activeUsers || 0} active (30 days)`
      },
      {
        title: 'Blog Posts',
        value: analytics.totalBlogPosts,
        icon: 'fas fa-blog',
        color: 'warning',
        subtitle: `${analytics.publishedBlogPosts} published`
      }
    ];

    return (
      <Row className="mb-4">
        {cards.map((card, index) => (
          <Col md={3} key={index} className="mb-3">
            <Card className="h-100 analytics-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-subtitle mb-2 text-muted">{card.title}</h6>
                    <h2 className={`text-${card.color} mb-0`}>
                      {formatNumber(card.value)}
                    </h2>
                    <small className="text-muted">{card.subtitle}</small>
                  </div>
                  <div className={`text-${card.color}`}>
                    <i className={`${card.icon} fa-2x`}></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderSurveyAnalytics = () => {
    if (!analytics?.surveyAnalytics) return null;

    const colors = getChartColors();
    
    // Popular surveys chart
    const popularSurveysData = {
      labels: analytics.surveyAnalytics.popularSurveys?.slice(0, 5).map(s => 
        s.title.length > 20 ? s.title.substring(0, 20) + '...' : s.title
      ) || [],
      datasets: [{
        label: 'Responses',
        data: analytics.surveyAnalytics.popularSurveys?.slice(0, 5).map(s => s.responseCount) || [],
        backgroundColor: colors.primary,
        borderColor: colors.primary,
        borderWidth: 1
      }]
    };

    // Completion rates doughnut
    const completionData = analytics.surveyAnalytics.completionRates || {};
    const completionRatesData = {
      labels: Object.keys(completionData).slice(0, 5),
      datasets: [{
        data: Object.values(completionData).slice(0, 5),
        backgroundColor: [
          colors.primary,
          colors.success,
          colors.warning,
          colors.danger,
          colors.info
        ]
      }]
    };

    return (
      <Row className="mb-4">
        <Col md={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-trophy me-2"></i>
                Most Popular Surveys
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Bar 
                  data={popularSurveysData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }} 
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-percentage me-2"></i>
                Completion Rates
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Doughnut 
                  data={completionRatesData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' }
                    }
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderTimeAnalytics = () => {
    if (!analytics?.timeAnalytics) return null;

    const colors = getChartColors();
    
    // Responses over time
    const responsesByDay = analytics.timeAnalytics.responsesByDay || {};
    const sortedDates = Object.keys(responsesByDay).sort();
    
    const responsesOverTimeData = {
      labels: sortedDates.map(date => new Date(date).toLocaleDateString()),
      datasets: [{
        label: 'Survey Responses',
        data: sortedDates.map(date => responsesByDay[date]),
        borderColor: colors.success,
        backgroundColor: colors.success + '20',
        fill: true,
        tension: 0.4
      }]
    };

    // Peak hours
    const responsesByHour = analytics.timeAnalytics.responsesByHour || {};
    const hourlyData = {
      labels: Array.from({length: 24}, (_, i) => `${i}:00`),
      datasets: [{
        label: 'Responses by Hour',
        data: Array.from({length: 24}, (_, i) => responsesByHour[i] || 0),
        backgroundColor: colors.info,
        borderColor: colors.info,
        borderWidth: 1
      }]
    };

    return (
      <Row className="mb-4">
        <Col md={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-line me-2"></i>
                Survey Responses (Last 30 Days)
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Line 
                  data={responsesOverTimeData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-clock me-2"></i>
                Peak Activity Hours
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Bar 
                  data={hourlyData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderDetailedTables = () => {
    if (!analytics) return null;

    return (
      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-list me-2"></i>
                Survey Performance
              </h5>
            </Card.Header>
            <Card.Body>
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Survey</th>
                    <th>Responses</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.surveyAnalytics?.popularSurveys?.slice(0, 10).map((survey, index) => (
                    <tr key={index}>
                      <td>
                        <div>
                          <strong>{survey.title}</strong>
                          <br />
                          <small className="text-muted">ID: {survey.surveyId}</small>
                        </div>
                      </td>
                      <td>
                        <Badge bg="primary">{survey.responseCount}</Badge>
                      </td>
                      <td>
                        <Badge bg={survey.active ? 'success' : 'secondary'}>
                          {survey.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-bar me-2"></i>
                Content Summary
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Surveys This Week</span>
                  <Badge bg="primary">{analytics.contentAnalytics?.surveysThisWeek || 0}</Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Blog Posts This Week</span>
                  <Badge bg="success">{analytics.contentAnalytics?.blogsThisWeek || 0}</Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Published Blogs</span>
                  <Badge bg="info">{analytics.contentAnalytics?.publishedBlogPosts || 0}</Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span>Draft Blogs</span>
                  <Badge bg="warning">{analytics.contentAnalytics?.draftBlogPosts || 0}</Badge>
                </div>
              </div>
              
              <hr />
              
              <h6>User Engagement</h6>
              <div className="mb-2">
                <div className="d-flex justify-content-between">
                  <small>Active Users (30 days)</small>
                  <small>{analytics.userAnalytics?.activeUsers || 0} / {analytics.totalUsers}</small>
                </div>
                <ProgressBar 
                  now={((analytics.userAnalytics?.activeUsers || 0) / (analytics.totalUsers || 1)) * 100}
                  variant="success"
                  className="mb-2"
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading analytics...</p>
      </Container>
    );
  }

  return (
    <>
      <SEOHead 
        title="Analytics Dashboard - Admin"
        description="Comprehensive analytics dashboard for website performance, user engagement, and content statistics."
      />
      <Container className="mt-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1>
                  <i className="fas fa-chart-line me-2 text-primary"></i>
                  Analytics Dashboard
                </h1>
                <p className="text-muted">
                  Comprehensive insights into your website's performance
                </p>
              </div>
              <div className="text-end">
                <small className="text-muted">
                  Last updated: {new Date().toLocaleString()}
                </small>
              </div>
            </div>
          </Col>
        </Row>

        {/* Error State */}
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Analytics Content */}
        {analytics && (
          <>
            {/* Overview Cards */}
            {renderOverviewCards()}

            {/* Survey Analytics */}
            {renderSurveyAnalytics()}

            {/* Time Analytics */}
            {renderTimeAnalytics()}

            {/* Detailed Tables */}
            {renderDetailedTables()}
          </>
        )}

        {/* Google Analytics Integration Notice */}
        <Row className="mt-5">
          <Col>
            <Card className="border-info">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <i className="fab fa-google fa-2x text-info me-3"></i>
                  <div>
                    <h5 className="mb-1">Google Analytics Integration</h5>
                    <p className="mb-0 text-muted">
                      This dashboard shows internal analytics. For detailed web traffic analysis, 
                      check your Google Analytics dashboard for page views, user behavior, and acquisition data.
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <style jsx>{`
          .analytics-card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            border: 1px solid #dee2e6;
          }
          
          .analytics-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
        `}</style>
      </Container>
    </>
  );
};

export default AnalyticsDashboard;